import { API } from '../Auth';

const EP = 'discoverRuns';
const BASE = '/api/v1/service-capabilities';

/**
 * Fetch per-role capabilities from the platform API. Used to gate the Mask and Encrypt
 * buttons on file data sources — files don't have JDBC drivers, so the JDBC-driver
 * check would always return false.
 *
 * Response shape (only the keys we use):
 *   {
 *     "masking": [{ serviceId, name, supportsCancellation, supportedFileFormats: ["CSV","TSV","PARQUET"] }, ...],
 *     "encrypt": [{ ... }],
 *     "discover": [{ ... }],
 *     ...
 *   }
 *
 * On failure (network, 5xx, anything), returns `{}` so callers treat it as "no capability
 * info" and fall back to the existing JDBC-driver path for non-file connections.
 */
export async function getServiceCapabilities() {
  try {
    const response = await API.get(EP, BASE);
    return response && typeof response === 'object' ? response : {};
  } catch (e) {
    return {};
  }
}

/**
 * Returns the union of `supportedFileFormats` across every enabled registration for the
 * given role (lowercased, e.g. "masking" or "encrypt"). Empty array if the role isn't
 * present or no service advertised file support.
 */
export function supportedFileFormatsForRole(capabilities, role) {
  const entries = capabilities && Array.isArray(capabilities[role]) ? capabilities[role] : [];
  const union = new Set();
  entries.forEach((entry) => {
    const formats = entry && Array.isArray(entry.supportedFileFormats) ? entry.supportedFileFormats : [];
    formats.forEach((f) => {
      if (typeof f === 'string' && f.trim() !== '') {
        union.add(f.trim().toUpperCase());
      }
    });
  });
  return Array.from(union);
}

/**
 * Returns the deduped union of `supportedFileTypes` ({ extension, name }) across every enabled
 * registration for the given role (e.g. "discover" or "masking"). Extensions are lowercased and
 * stripped of a leading dot; duplicate extensions collapse (first name wins). Empty array when the
 * role isn't present or no service advertised any file types — callers treat empty as "unknown, do
 * not gate", matching the backend's null-means-no-claim semantics.
 */
export function supportedFileTypesForRole(capabilities, role) {
  const entries = capabilities && Array.isArray(capabilities[role]) ? capabilities[role] : [];
  const byExt = new Map();
  entries.forEach((entry) => {
    const types = entry && Array.isArray(entry.supportedFileTypes) ? entry.supportedFileTypes : [];
    types.forEach((t) => {
      if (!t || typeof t.extension !== 'string') return;
      const ext = t.extension.trim().toLowerCase().replace(/^\./, '');
      if (ext === '' || byExt.has(ext)) return;
      byExt.set(ext, { extension: ext, name: typeof t.name === 'string' ? t.name.trim() : ext });
    });
  });
  return Array.from(byExt.values());
}

/**
 * Set of supported lowercase extensions for a role, for a quick membership check against a
 * path's parsed extension.
 */
export function supportedExtensionSet(capabilities, role) {
  return new Set(supportedFileTypesForRole(capabilities, role).map((t) => t.extension));
}

/**
 * Distinct display names of the supported types for a role, in declaration order (deduped).
 * Used to render the "supported types" line in the editor's "not supported" message.
 */
export function supportedTypeNamesForRole(capabilities, role) {
  const names = [];
  const seen = new Set();
  supportedFileTypesForRole(capabilities, role).forEach((t) => {
    if (t.name && !seen.has(t.name)) {
      seen.add(t.name);
      names.push(t.name);
    }
  });
  return names;
}
